import React, { memo, useState } from 'react'
import { startCase } from 'lodash'
import { documents, capabilities } from '@homevest/utils'
import moment from 'moment'
import PropTypes from 'prop-types'
import { Heading, Button, HStack, IconButton, Tooltip } from '@chakra-ui/react'
import { CheckIcon, XMarkIcon, PencilIcon } from '@heroicons/react/24/outline'

import { BodyText } from 'components/Toolkit'

import ApproveDocumentModal from './approve-document-modal'
import UpdateDocumentModal from './update-document-modal'
import { useSelector } from 'react-redux'
import { hasCapability } from 'lib/admin-perms'
import axios from 'lib/axios'
import SupplementalDocs from 'components/SupplementalDocs'

const { USER_DOC_VIEWER, APPLICATION_REVIEWER } = capabilities.CAPABILITY_TYPES

const { DOCUMENT_STATUSES } = documents

function UserDocuments({
  rentalAppId,
  userId,
  documents,
  isSaving,
  setDocumentReviewStatus,
  updateDocumentReviewStatus,
  addDocument
}) {
  const isDocViewer = useSelector((store) => hasCapability(store.admin, USER_DOC_VIEWER))
  const isReviewer = useSelector((store) => hasCapability(store.admin, APPLICATION_REVIEWER))
  const [approveDocId, setApproveDocId] = useState(null)
  const [updateDocId, setUpdateDocId] = useState(null)

  return (
    <div>
      <HStack mb={3} mt={4} alignItems='center' justifyContent='space-between'>
        <Heading size='md' as='h3'>
          Documents
        </Heading>
        <HStack spacing={2}>
          {isReviewer && <SupplementalDocs rentalAppId={rentalAppId} filterUserId={userId} />}
          {addDocument && (
            <Button colorScheme={'teal'} variant={'outline'} onClick={addDocument} size='xs'>
              + Add
            </Button>
          )}
        </HStack>
      </HStack>
      {documents.length === 0 && (
        <div>
          <BodyText>No documents uploaded! 📑</BodyText>
        </div>
      )}
      {documents.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>
                <BodyText weight='600' size='s'>
                  Name
                </BodyText>
              </th>
              <th>
                <BodyText weight='600' size='s'>
                  Status
                </BodyText>
              </th>
              <th style={{ padding: '0 10px' }}>
                <BodyText weight='600' size='s'>
                  Uploaded
                </BodyText>
              </th>
              <th>
                <BodyText weight='600' size='s'>
                  Reviewed
                </BodyText>
              </th>
              <th style={{ padding: '0 10px' }}>
                <BodyText weight='600' size='s'>
                  Reviewed By
                </BodyText>
              </th>
              <th>
                <BodyText weight='600' size='s'>
                  Notes
                </BodyText>
              </th>
            </tr>
          </thead>
          <tbody>
            {documents
              .sort((a, b) => {
                if (a.status < b.status) {
                  return -1
                } else if (a.status > b.status) {
                  return 1
                } else {
                  return a.type < b.type ? -1 : 1
                }
              })
              .map((doc) => (
                <tr key={doc.id}>
                  <td style={{ paddingRight: '10px' }}>
                    {' '}
                    <BodyText size='s'>
                      {isDocViewer ? (
                        <Button
                          variant='link'
                          textColor='black'
                          fontWeight='medium'
                          fontSize='sm'
                          onClick={async () => {
                            const res = await axios.get(`/admin/documents/${doc.id}/url`)

                            if (res.data.url) {
                              window.open(res.data.url, '_blank')
                            }
                          }}
                        >
                          {doc.friendly_name}
                        </Button>
                      ) : (
                        doc.friendly_name
                      )}
                    </BodyText>
                  </td>
                  <td>
                    <BodyText size='s'>{startCase(doc.status)}</BodyText>
                  </td>
                  <td style={{ padding: '0 10px' }}>
                    <BodyText size='s'>{moment(doc.created_at).format('YYYY-MM-DD')}</BodyText>
                  </td>
                  <td>
                    <BodyText size='s'>
                      {doc.reviewed_at ? moment(doc.reviewed_at).format('YYYY-MM-DD') : 'N/A'}
                    </BodyText>
                  </td>
                  <td style={{ padding: '0 10px' }}>
                    <BodyText size='s'>
                      {doc.reviewed_by
                        ? `${doc.reviewed_by.first_name} ${doc.reviewed_by.last_name}`
                        : 'N/A'}
                    </BodyText>
                  </td>
                  <td>
                    <BodyText size='s'>{doc.review_notes ? `${doc.review_notes}` : 'N/A'}</BodyText>
                  </td>

                  <td style={{ padding: '0 10px 10px 10px' }}>
                    {setDocumentReviewStatus &&
                      isDocViewer &&
                      doc.status !== DOCUMENT_STATUSES.VALID && (
                        <Tooltip
                          label={'Approve'}
                          placement='top'
                          bg='green.50'
                          color='green'
                          fontSize='xs'
                          p={1}
                          borderRadius='md'
                        >
                          <IconButton
                            disabled={isSaving}
                            size='xs'
                            variant='outline'
                            colorScheme='green'
                            icon={<CheckIcon className='h-4 w-4' />}
                            onClick={() => setApproveDocId(doc.id)}
                            aria-label='Approve document'
                          />
                        </Tooltip>
                      )}
                    {updateDocumentReviewStatus &&
                      isDocViewer &&
                      doc.status === DOCUMENT_STATUSES.VALID && (
                        <Tooltip
                          label={'Edit'}
                          placement='top'
                          bg='teal.50'
                          color='teal'
                          fontSize='xs'
                          p={1}
                          borderRadius='md'
                        >
                          <IconButton
                            disabled={isSaving}
                            size='xs'
                            variant='outline'
                            colorScheme='teal'
                            icon={<PencilIcon className='h-4 w-4' />}
                            onClick={() => setUpdateDocId(doc.id)}
                            aria-label='Edit document'
                          />
                        </Tooltip>
                      )}
                  </td>
                  <td style={{ padding: '0 10px 10px 0px' }}>
                    {setDocumentReviewStatus && isDocViewer && (
                      <Tooltip
                        label={'Reject'}
                        placement='top'
                        bg='red.50'
                        color='red'
                        fontSize='xs'
                        p={1}
                        borderRadius='md'
                      >
                        <IconButton
                          disabled={isSaving}
                          size='xs'
                          variant='outline'
                          colorScheme='red'
                          icon={<XMarkIcon className='h-4 w-4' />}
                          onClick={() => setDocumentReviewStatus(doc.id, DOCUMENT_STATUSES.INVALID)}
                          aria-label='Reject document'
                        />
                      </Tooltip>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      )}
      {approveDocId ? (
        <ApproveDocumentModal
          close={() => setApproveDocId(null)}
          approve={(notes) => {
            setDocumentReviewStatus(approveDocId, DOCUMENT_STATUSES.VALID, notes || undefined)
            setApproveDocId(null)
          }}
        />
      ) : null}

      {updateDocId ? (
        <UpdateDocumentModal
          close={() => setUpdateDocId(null)}
          document={documents.find((i) => i.id === updateDocId)}
          update={(notes) => {
            updateDocumentReviewStatus(updateDocId, notes)
            setUpdateDocId(null)
          }}
        />
      ) : null}
    </div>
  )
}

UserDocuments.propTypes = {
  documents: PropTypes.array.isRequired,
  isSaving: PropTypes.bool.isRequired,
  updateDocumentReviewStatus: PropTypes.func,
  setDocumentReviewStatus: PropTypes.func,
  addDocument: PropTypes.func
}

export default memo(UserDocuments)
